import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { CalendarCheck, Users, Clock, Ban, ClipboardList } from "lucide-react";

export function CircleStatsCard() {
  const { data: stats } = useQuery({
    queryKey: ["circle-stats"],
    queryFn: async () => {
      const [eventsResult, pendingEventsResult, bookingsResult, bansResult, applicationsResult, pendingAppsResult] = await Promise.all([
        supabase
          .from("circle_events")
          .select("id", { count: "exact", head: true })
          .eq("status", "approved"),
        supabase
          .from("circle_events")
          .select("id", { count: "exact", head: true })
          .eq("status", "pending"),
        supabase
          .from("circle_bookings")
          .select("id", { count: "exact", head: true })
          .eq("status", "confirmed"),
        supabase
          .from("circle_bans")
          .select("id", { count: "exact", head: true })
          .is("unbanned_at", null),
        supabase
          .from("circle_applications")
          .select("id", { count: "exact", head: true }),
        supabase
          .from("circle_applications")
          .select("id", { count: "exact", head: true })
          .eq("status", "pending"),
      ]);

      return {
        activeEvents: eventsResult.count || 0,
        pendingEvents: pendingEventsResult.count || 0,
        totalBookings: bookingsResult.count || 0,
        activeBans: bansResult.count || 0,
        totalApplications: applicationsResult.count || 0,
        pendingApplications: pendingAppsResult.count || 0,
      };
    },
  });

  const statItems = [
    {
      label: "Applications",
      value: stats?.pendingApplications || 0,
      subValue: `of ${stats?.totalApplications || 0} total`,
      icon: ClipboardList,
      color: "text-violet-500",
    },
    {
      label: "Active Events",
      value: stats?.activeEvents || 0,
      icon: CalendarCheck,
      color: "text-success",
    },
    {
      label: "Pending Events",
      value: stats?.pendingEvents || 0,
      icon: Clock,
      color: "text-warning",
    },
    {
      label: "Total Bookings",
      value: stats?.totalBookings || 0,
      icon: Users,
      color: "text-info",
    },
    {
      label: "Active Bans",
      value: stats?.activeBans || 0,
      icon: Ban,
      color: "text-destructive",
    },
  ];

  return (
    <div className="grid gap-4 md:grid-cols-5">
      {statItems.map((item) => (
        <Card key={item.label}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {item.label}
                </p>
                <p className="text-2xl font-bold">{item.value}</p>
                {"subValue" in item && item.subValue && (
                  <p className="text-xs text-muted-foreground">{item.subValue}</p>
                )}
              </div>
              <item.icon className={`h-8 w-8 ${item.color} opacity-80`} />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
